'use client';

import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { Button } from './Button';
import { EmptyState } from './EmptyState';
import styles from './NotificationList.module.css';

export interface NotificationMenuItem {
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

export interface NotificationItemProps {
  /** Optional icon element displayed in the circle */
  icon?: React.ReactNode;
  /** Date/time text (e.g., "2 hours ago") */
  date: string;
  /** Main content of the notification */
  content: React.ReactNode;
  /** Whether this notification is unread — shows "New!" badge */
  isUnread?: boolean;
  /**
   * Pre-formatted read timestamp (e.g., "Read 2 hours ago"). Shown next to the
   * date for read notifications; ignored when `isUnread` is true.
   */
  readAt?: string;
  /** Label for the action button (e.g., "View float", "Send email") */
  actionLabel?: string;
  /** Click handler for the action button */
  onAction?: () => void;
  /** Items for the kebab menu on the right of the card */
  menuItems?: NotificationMenuItem[];
  /** Accent color for the left border (e.g., "#13319A") */
  accentColor?: string;
  /** Background color for the icon circle (e.g., "#EDF4FE") */
  iconBg?: string;
}

export interface NotificationListProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Array of notification items to display */
  items: NotificationItemProps[];
  /** Maximum number of items to show before "show all" (default: 6) */
  maxVisible?: number;
  /** Label for the "show all" button (default: "View all notifications") */
  showAllLabel?: string;
  /**
   * Called when the "show all" button is clicked instead of expanding the
   * list in place — use this to navigate to a full notifications page.
   * When omitted, the button falls back to expanding `items` inline.
   */
  onShowAll?: () => void;
  /** Content to display when items array is empty */
  emptyMessage?: React.ReactNode;
  /** Additional class names */
  className?: string;
}

function KebabIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}

function KebabMenu({ items }: { items: NotificationMenuItem[] }) {
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuPos) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setMenuPos(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuPos]);

  function handleOpen() {
    if (menuPos) { setMenuPos(null); return; }
    const rect = btnRef.current!.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  }

  return (
    <div className={styles.kebabWrap}>
      <button ref={btnRef} className={styles.kebabBtn} onClick={handleOpen} aria-label="More actions">
        <KebabIcon />
      </button>
      {menuPos && (
        <div
          ref={menuRef}
          className={styles.kebabMenu}
          style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
        >
          {items.map((item, idx) => (
            <button
              key={idx}
              className={clsx(styles.kebabMenuItem, item.destructive && styles.kebabMenuItemDestructive)}
              onClick={() => { item.onClick(); setMenuPos(null); }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const NotificationList = React.forwardRef<HTMLDivElement, NotificationListProps>(
  (
    {
      items,
      maxVisible = 6,
      showAllLabel = 'View all notifications',
      onShowAll,
      emptyMessage,
      className,
      ...props
    },
    ref
  ) => {
    const [showAll, setShowAll] = useState(false);
    const handleShowAll = onShowAll ?? (() => setShowAll(true));

    const displayedItems = showAll ? items : items.slice(0, maxVisible);
    const hasMore = items.length > maxVisible;

    if (!items.length) {
      return (
        <div ref={ref} className={clsx(styles.list, className)} {...props}>
          <EmptyState
            title={typeof emptyMessage === 'string' ? emptyMessage : 'No notifications'}
            description="You're all caught up. New activity will appear here."
          />
        </div>
      );
    }

    return (
      <div ref={ref} className={clsx(styles.list, className)} {...props}>
        {displayedItems.map((item, idx) => (
          <div
            key={idx}
            className={clsx(styles.row, item.isUnread && styles.unread)}
            style={{
              '--accent': item.accentColor || 'var(--action-product)',
            } as React.CSSProperties}
          >
            {item.icon && (
              <div
                className={styles.iconCircle}
                style={{ backgroundColor: item.iconBg || 'var(--bg-muted)' }}
              >
                {item.icon}
              </div>
            )}

            <div className={styles.body}>
              <div className={styles.meta}>
                <span className={styles.date}>{item.date}</span>
                {!item.isUnread && item.readAt && (
                  <span className={styles.readAt}>{item.readAt}</span>
                )}
              </div>
              <div className={styles.contentRow}>
                {item.isUnread && <span className={styles.chip}>New!</span>}
                <div className={styles.content}>{item.content}</div>
              </div>
            </div>

            {item.actionLabel && (
              <div className={styles.action}>
                <Button variant="secondary" size="sm" onClick={item.onAction}>
                  {item.actionLabel}
                </Button>
              </div>
            )}
            {item.menuItems && item.menuItems.length > 0 && (
              <KebabMenu items={item.menuItems} />
            )}
          </div>
        ))}

        {hasMore && !showAll && (
          <div className={styles.showAllContainer}>
            <Button variant="tertiary" onClick={handleShowAll}>
              {showAllLabel}
            </Button>
          </div>
        )}
      </div>
    );
  }
);

NotificationList.displayName = 'NotificationList';

export default NotificationList;
